import { useWatch, Control } from 'react-hook-form';
import { useMemo } from 'react';

/**
 * Custom hook to watch alert criteria fields and determine which dynamic fields should be rendered.
 * This helps control conditional rendering of fields based on criteria values.
 *
 * @param control - React Hook Form control object
 * @returns Object containing watched values and computed field visibility states
 */
export const useAlertCriteriaWatch = (control: Control<Record<string, any>>) => {
  // Watch all relevant criteria fields including threshold variants
  const watchedFields = useWatch({
    control,
    name: [
      'criteria.data_column',
      'data_column',
      'criteria.operator',
      'operator',
      'criteria.threshold',
      'threshold',
      'criteria.threshold_float',
      'threshold_float',
      'criteria.threshold_int',
      'threshold_int',
      'criteria.threshold_string',
      'threshold_string',
      'criteria.threshold_reference',
      'threshold_reference',
      'criteria.threshold_column',
      'threshold_column',
      'criteria.threshold_column_value',
      'threshold_column_value',
      'compare_type',
    ],
  });

  // Destructure watched values
  const [
    criteriaDataColumn,
    dataColumn,
    criteriaOperator,
    operator,
    criteriaThreshold,
    threshold,
    criteriaThresholdFloat,
    thresholdFloat,
    criteriaThresholdInt,
    thresholdInt,
    criteriaThresholdString,
    thresholdString,
    criteriaThresholdReference,
    thresholdReference,
    criteriaThresholdColumn,
    thresholdColumn,
    criteriaThresholdColumnValue,
    thresholdColumnValue,
    compareType,
  ] = watchedFields;

  // Normalize values (handle both nested criteria.* and flat field names)
  const normalizedValues = useMemo(
    () => ({
      dataColumn: criteriaDataColumn ?? dataColumn,
      operator: criteriaOperator ?? operator,
      threshold:
        criteriaThreshold ??
        threshold ??
        criteriaThresholdFloat ??
        thresholdFloat ??
        criteriaThresholdInt ??
        thresholdInt ??
        criteriaThresholdString ??
        thresholdString,
      thresholdReference: criteriaThresholdReference ?? thresholdReference,
      thresholdColumn: criteriaThresholdColumn ?? thresholdColumn,
      thresholdColumnValue: criteriaThresholdColumnValue ?? thresholdColumnValue,
      compareType,
    }),
    [
      criteriaDataColumn,
      dataColumn,
      criteriaOperator,
      operator,
      criteriaThreshold,
      threshold,
      criteriaThresholdFloat,
      thresholdFloat,
      criteriaThresholdInt,
      thresholdInt,
      criteriaThresholdString,
      thresholdString,
      criteriaThresholdReference,
      thresholdReference,
      criteriaThresholdColumn,
      thresholdColumn,
      criteriaThresholdColumnValue,
      thresholdColumnValue,
      compareType,
    ],
  );

  // Compute field visibility based on criteria values
  const fieldVisibility = useMemo(() => {
    const { thresholdReference } = normalizedValues;

    return {
      // Show threshold_column field only when threshold_reference is 'individual_specified'
      shouldShowThresholdColumn: thresholdReference === 'individual_specified',

      // Show threshold_column_value field when threshold_column is selected
      shouldShowThresholdColumnValue: Boolean(normalizedValues.thresholdColumn),

      // Show alert_expiration field (could be conditional in the future)
      shouldShowAlertExpiration: true,

      // Additional computed states can be added here based on requirements
      isIndividualThreshold: thresholdReference === 'individual_specified',
      isTotalThreshold: thresholdReference === 'total',
    };
  }, [normalizedValues]);

  return {
    ...normalizedValues,
    fieldVisibility,
    // Provide raw watched values for additional flexibility
    rawWatchedFields: watchedFields,
  };
};

export default useAlertCriteriaWatch;
